import type { PublicBrand, ResolvedBrand } from '@tendedloop/shared/brand-public';
import { HINT_KEY, loginPathFor, validHandle } from '@/lib/brandSources';
import { readAttribution, type AdAttribution } from './attribution';
import { useLang, type PortalLang } from './lang';
const raw = import.meta.env.VITE_API_URL || 'http://localhost:3001';
export const API_BASE = raw.endsWith('/api') ? raw : `${raw}/api`;
const BASE = API_BASE;

/**
 * The portal's preview for the dashboard's Brand page (/preview) answers every
 * call from sample data: it never reaches the API, storage or a session.
 */
type PreviewAnswer = (method: string, path: string) => unknown;
let previewAnswer: PreviewAnswer | null = null;
export function answerFromPreview(answer: PreviewAnswer | null) {
  previewAnswer = answer;
}

/** How long a signed-out screen waits for a brand before showing TendedLoop's look. */
const BRAND_TIMEOUT_MS = 2500;

async function withTimeout(run: (signal: AbortSignal) => Promise<Response>): Promise<Response | undefined> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), BRAND_TIMEOUT_MS);
  try {
    return await run(ctrl.signal);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The public brand for an org hint: `GET /public/brand/:handle`. A simple GET
 * (no Authorization or custom headers, no credentials), so no preflight.
 * `{ brand }`, `{ redirect }`, null (no brand to show), or undefined (no answer in time).
 */
export async function fetchPublicBrand(
  handle: string,
  lang: PortalLang = 'he',
): Promise<{ brand: PublicBrand } | { redirect: string } | null | undefined> {
  if (previewAnswer) return null;
  // `lang` names the brand in the page's language when it has a name for it (names.ar).
  const res = await withTimeout((signal) =>
    fetch(`${BASE}/public/brand/${encodeURIComponent(handle)}?lang=${lang}`, { credentials: 'omit', signal }),
  );
  if (!res) return undefined;
  if (res.status === 404) return null;
  if (!res.ok) return undefined;
  const json = await res.json().catch(() => null);
  const data = json?.data;
  if (data && typeof data.redirect === 'string') return { redirect: data.redirect };
  if (data?.brand && typeof data.brand === 'object') return { brand: data.brand as PublicBrand };
  return null;
}

/**
 * The brand of the office that sent a sign-in link (valid or expired):
 * `POST /resident-portal/auth/brand`. Never uses up the link. The brand, null
 * (none to show), or undefined (no answer in time).
 */
export async function fetchTokenBrand(token: string, lang: PortalLang = 'he'): Promise<PublicBrand | null | undefined> {
  if (previewAnswer) return null;
  const res = await withTimeout((signal) =>
    fetch(`${BASE}/resident-portal/auth/brand`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, lang }),
      credentials: 'omit',
      signal,
    }),
  );
  if (!res?.ok) return undefined;
  const json = await res.json().catch(() => null);
  const brand = json?.data?.brand;
  return brand && typeof brand === 'object' ? (brand as PublicBrand) : null;
}

/** The org's home-screen app manifest (the portal links it when it knows the org). */
/** The org's manifest, opening on this page's own origin (the API allows only the portal's). */
export const manifestUrlFor = (handle: string) =>
  `${BASE}/public/manifest/${encodeURIComponent(handle)}?at=${encodeURIComponent(window.location.origin)}`;
const TOKEN_KEY = 'resident_token';
const REFRESH_KEY = 'resident_refresh';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode */
  }
}

function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/** Store a signed-in session (or clear it with null). */
export function setSession(s: { accessToken: string; refreshToken?: string } | null): void {
  setToken(s?.accessToken ?? null);
  try {
    if (s?.refreshToken) localStorage.setItem(REFRESH_KEY, s.refreshToken);
    else if (!s) localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* private mode */
  }
}

// The access token lasts an hour; the refresh token keeps the resident signed
// in on this device for months. One renewal at a time, shared by every caller.
let renewing: Promise<boolean> | null = null;
function renewSession(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);
  renewing ??= fetch(`${BASE}/resident-portal/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async (res) => {
      const json = await res.json().catch(() => ({}));
      // Another tab renewed with the same token a moment ago: it stored the new
      // pair, so use that instead of treating this as a failure.
      if (res.status === 409) return getRefreshToken() !== refreshToken;
      if (!res.ok || !json?.data?.accessToken) {
        // Only a definite "no" ends the session; a network blip keeps it for later.
        if (res.status === 401) setSession(null);
        return false;
      }
      setSession(json.data);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      renewing = null;
    });
  return renewing;
}

/** fetch with the resident's token; on 401 renews the session once and retries. */
async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers);
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(`${BASE}${path}`, { ...init, headers });
  };
  let res = await send();
  if (res.status === 401 && !path.startsWith('/resident-portal/auth/') && (await renewSession())) {
    res = await send();
  }
  if (res.status === 401) onUnauthorized(path);
  return res;
}

export interface Profile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  email: string | null;
  gender: 'MALE' | 'FEMALE' | 'OTHER' | 'UNKNOWN';
  dateOfBirth: string | null;
  phone: string | null;
  whatsappPhone: string | null;
  homeAddress: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelation: string | null;
  studyInstitution: string | null;
  faculty: string | null;
  degreeType: string | null;
  studyYear: number | null;
  studyStartDate: string | null;
  studyEndDate: string | null;
  bankName: string | null;
  bankBranch: string | null;
  nationality: string | null;
  hasNationalId: boolean;
  hasBankAccount: boolean;
  hasPassport: boolean;
  /** Last 4 digits of what is on file ("••••1234"); the full number never comes back. */
  nationalIdMasked: string | null;
  passportMasked: string | null;
  bankAccountMasked: string | null;
  activated: boolean;
  /** Left an email on the portal's front door and is still looking: the portal opens on home. */
  prospect?: boolean;
}

/** What the front door of a published office sends with an email (prospect landing). */
export interface LeadFields {
  o?: string;
  consentVersion?: string;
  marketingOptIn?: boolean;
  turnstileToken?: string;
  /** Honeypot: always empty for people. */
  website?: string;
  /** The page's language: the notice shown (kept with the consent) and the email's language. */
  lang?: PortalLang;
}

/** Wizard choices from the operator's own records (values used by 2+ residents). */
export type OptionField = 'degreeType' | 'faculty' | 'studyInstitution' | 'bankName';
export type FieldOptions = Record<OptionField, string[]>;

export interface Room {
  building: string | null;
  apartment: string;
  floor: number;
  bedNumber: number;
  /** When the key was handed over (ISO), else null: the move-in welcome. */
  keyHandedAt?: string | null;
}

export type ProfilePatch = Partial<
  Record<
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'gender'
    | 'dateOfBirth'
    | 'phone'
    | 'whatsappPhone'
    | 'homeAddress'
    | 'emergencyContactName'
    | 'emergencyContactPhone'
    | 'emergencyContactRelation'
    | 'studyInstitution'
    | 'faculty'
    | 'degreeType'
    | 'studyStartDate'
    | 'studyEndDate'
    | 'nationalId'
    | 'nationality'
    | 'passportNumber'
    | 'bankName'
    | 'bankBranch'
    | 'bankAccount',
    string
  > & { studyYear: number }
>;

/** An API failure that keeps the server's error code (e.g. LEASE_DOCUMENT_CHANGED). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
  }
}

// A stored session the server no longer accepts (expired / revoked): drop it and
// send the resident to sign in again, instead of leaving every screen erroring.
function onUnauthorized(path: string): void {
  if (path.startsWith('/resident-portal/auth/') || !getToken()) return;
  setSession(null);
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign(loginPathFor(window.location.pathname, sessionHint(), { expired: '1' }));
  }
}

/** The org hint kept for this browser session (from ?o=), if any. */
function withAttribution(consentVersion: string | undefined): { attribution?: AdAttribution } {
  const attribution = consentVersion ? readAttribution() : undefined;
  return attribution ? { attribution } : {};
}

function sessionHint(): string | null {
  try {
    return validHandle(sessionStorage.getItem(HINT_KEY));
  } catch {
    return null; // storage blocked
  }
}

/** No connection (dorm Wi-Fi, a tunnel): one clear message instead of a browser error. */
export const NETWORK_MESSAGE = 'אין חיבור לאינטרנט כרגע. מה שמילאת לא נמחק; נסו שוב בעוד רגע.';
const offline = () => new ApiError(NETWORK_MESSAGE, 0, 'NETWORK');

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (previewAnswer) return previewAnswer(method, path) as T;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await authedFetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw offline();
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new ApiError(json?.error || json?.message || `שגיאה (${res.status})`, res.status, json?.code);
  }
  return json.data as T;
}

/** A tour slot as the server offers it: when, and whether it is free (never how many are booked). */
export interface PortalTourSlot {
  date: string;
  start: string;
  end: string;
  free: boolean;
}

/**
 * Tours on home (GET /me/tour). `hidden`: show nothing (no tour while tours
 * are not published, a current resident, or the tour already took place).
 * `invite`: no tour yet (published only). `upcoming`: the person's tour.
 * `canChange`: it can move (the tour page is published, there are times to
 * pick). `canCancel`: it can be cancelled (also while not published; an
 * older API leaves it out, then it follows canChange).
 */
export type PortalTourView =
  | { state: 'hidden' }
  | {
      state: 'invite' | 'upcoming';
      tour: {
        id: string;
        start: string;
        end: string;
        unitTypeName: string | null;
        canChange: boolean;
        canCancel?: boolean;
      } | null;
      slots: PortalTourSlot[];
      unitTypes: { id: string; name: string }[];
      timeZone: string;
      buildingName: string | null;
    };

/** The housing office's contact details (any may be empty). */
export interface Office {
  name: string | null;
  phone: string | null;
  email: string | null;
  hours: string | null;
  /** The building's own brand, once TendedLoop verified it (resolved on the server). Not personal data. */
  brand?: ResolvedBrand | null;
}

/** A new session: tokens plus what the app shows first. */
export interface SignedIn {
  accessToken: string;
  refreshToken: string;
  resident: Profile;
  room: Room | null;
}

export type ResidentDocType = 'enrollment_certificate' | 'id_card' | 'id_appendix';
export interface ResidentDoc {
  id: string;
  docType: string | null;
  name: string;
  createdAt: string;
  readUrl: string | null;
}

// Upload a blob straight to the signed GCS URL (Content-Type must match what the
// server signed). Not routed through the API: it goes directly to storage.
export async function putToStorage(uploadUrl: string, blob: Blob): Promise<void> {
  let res: Response;
  try {
    res = await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type } });
  } catch {
    throw offline();
  }
  if (!res.ok) throw new Error(`שגיאת העלאה (${res.status})`);
}

export type LeaseStatus = 'none' | 'pending' | 'signed';
export interface LeaseInfo {
  status: LeaseStatus;
  contractNumber: string | null;
  monthlyRent: string | null;
  monthlyTotal?: string;
  coveredByPartner?: boolean;
  depositAmount: string | null;
  startDate: string | null;
  endDate: string | null;
  /** Name + ID on file: required before signing. */
  identityComplete: boolean;
  /** The resident's own details a template-prepared lease still needs before signing. */
  missingDetails: { key: string; label: string }[];
  /** Prepared from the operator's template; its name (e.g. "כתב התחייבות"). */
  fromTemplate: boolean;
  documentTitle: string | null;
  documentUrl: string | null;
  signedAt: string | null;
  /** Signed with the office on paper: signed, but there is no copy to view here. */
  signedOffline?: boolean;
  consentText: string;
}

// Fetch the lease PDF bytes through the authenticated API (not the private
// bucket) so the viewer never depends on GCS CORS.
export async function fetchLeaseFileBytes(): Promise<ArrayBuffer> {
  if (previewAnswer) return previewAnswer('GET', '/resident-portal/me/lease/file') as Promise<ArrayBuffer>;
  const res = await authedFetch('/resident-portal/me/lease/file');
  if (res.status === 403) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError(json?.error || 'נדרש אימות', 403, json?.code);
  }
  if (!res.ok) throw new Error(`שגיאה בטעינת החוזה (${res.status})`);
  return res.arrayBuffer();
}

export const api = {
  getLease: () => request<LeaseInfo>('GET', '/resident-portal/me/lease'),
  myTour: () => request<PortalTourView>('GET', '/resident-portal/me/tour'),
  // The language the card is shown in goes along: the tour emails follow it.
  bookTour: (slotStart: string, unitTypeId?: string, lang: PortalLang = 'he') =>
    request<PortalTourView>('POST', '/resident-portal/me/tour', {
      slotStart,
      ...(unitTypeId ? { unitTypeId } : {}),
      lang,
    }),
  rescheduleTour: (slotStart: string, lang: PortalLang = 'he') =>
    request<PortalTourView>('POST', '/resident-portal/me/tour/reschedule', { slotStart, lang }),
  cancelTour: (lang: PortalLang = 'he') => request<PortalTourView>('POST', '/resident-portal/me/tour/cancel', { lang }),
  // documentSha256: hash of the exact PDF bytes shown, so the server refuses to
  // seal a version the resident did not read.
  signLease: (signature: string, documentSha256?: string) =>
    request<{ status: 'signed'; contractNumber: string; signedAt: string; documentUrl: string | null }>(
      'POST',
      '/resident-portal/me/lease/sign',
      { signature, consent: true, ...(documentSha256 ? { documentSha256 } : {}) },
    ),
  // With the org hint, a published office's front door also takes new leads
  // (the notice version shown, the marketing opt-in, the bot check, the honeypot).
  requestLink: (email: string, next?: string, lead?: LeadFields) =>
    request<{ requested: true }>('POST', '/resident-portal/auth/request-link', {
      email,
      ...(next && next !== '/' ? { next } : {}),
      ...(lead ?? {}),
      // The ad that brought the visit, only with the front door's notice (a lead may be made).
      ...withAttribution(lead?.consentVersion),
    }),
  verify: (token: string) => request<SignedIn>('POST', '/resident-portal/auth/verify', { token }),
  // The session's org hint (?o=) picks among this Google email's records when it has several.
  google: (credential: string, lead?: Pick<LeadFields, 'consentVersion' | 'marketingOptIn' | 'lang'>) => {
    const o = sessionHint();
    return request<SignedIn>('POST', '/resident-portal/auth/google', {
      credential,
      ...(o ? { o } : {}),
      ...(o && lead ? { ...lead, ...withAttribution(lead.consentVersion) } : {}),
    });
  },
  signOut: () => request<{ signedOut: true }>('POST', '/resident-portal/me/sign-out', {}),
  // The brand's name in the portal's language (names.ar) when it has one.
  office: () => request<Office>('GET', `/resident-portal/me/office?lang=${useLang.getState().lang}`),
  confirmGoogle: (credential: string) =>
    request<{ accessToken: string }>('POST', '/resident-portal/me/confirm/google', { credential }),
  confirmEmail: (next: string) => request<{ sent: true }>('POST', '/resident-portal/me/confirm/email', { next }),
  me: () => request<Profile>('GET', '/resident-portal/me'),
  options: () => request<FieldOptions>('GET', '/resident-portal/me/options'),
  room: () => request<{ room: Room | null }>('GET', '/resident-portal/me/room'),
  updateMe: (patch: ProfilePatch) => request<Profile>('PATCH', '/resident-portal/me', patch),
  listDocs: () => request<{ documents: ResidentDoc[] }>('GET', '/resident-portal/me/documents'),
  docUploadUrl: (docType: ResidentDocType, mimeType: string, fileSize: number) =>
    request<{ uploadUrl: string; storageKey: string; expiresIn: number }>(
      'POST',
      '/resident-portal/me/documents/upload-url',
      { docType, mimeType, fileSize },
    ),
  createDoc: (docType: ResidentDocType, storageKey: string) =>
    request<ResidentDoc>('POST', '/resident-portal/me/documents', { docType, storageKey }),
};
